import { InventoryQueryParams } from '../interfaces/inventory-item.interface';

export const FILTER_RANGE_DEFAULTS = {
  MIN: 0,
  MAX: 1000,
} as const;

export const MIN_SEARCH_LENGTH = 2;

const isRangeChanged = (value: number | undefined, defaultValue: number): boolean =>
  value !== undefined && value !== defaultValue;

const normalizeRangeValue = (value: number | undefined, defaultValue: number): number =>
  value === undefined || value === null ? defaultValue : Number(value);

export function hasActiveFilters(filters: InventoryQueryParams): boolean {
  const hasTextFilters = Boolean(
    filters.productName ||
    filters.productLocation ||
    filters.brandName ||
    filters.categoryId ||
    filters.status ||
    filters.sortBy ||
    filters.sortOrder,
  );

  const hasRangeFilters = Boolean(
    isRangeChanged(filters.minPrice, FILTER_RANGE_DEFAULTS.MIN) ||
    isRangeChanged(filters.maxPrice, FILTER_RANGE_DEFAULTS.MAX) ||
    isRangeChanged(filters.minAvailableAmount, FILTER_RANGE_DEFAULTS.MIN) ||
    isRangeChanged(filters.maxAvailableAmount, FILTER_RANGE_DEFAULTS.MAX) ||
    isRangeChanged(filters.minReservedAmount, FILTER_RANGE_DEFAULTS.MIN) ||
    isRangeChanged(filters.maxReservedAmount, FILTER_RANGE_DEFAULTS.MAX) ||
    isRangeChanged(filters.minOnHandAmount, FILTER_RANGE_DEFAULTS.MIN) ||
    isRangeChanged(filters.maxOnHandAmount, FILTER_RANGE_DEFAULTS.MAX),
  );

  return hasTextFilters || hasRangeFilters;
}

export function isValidSearchTerm(term: string): boolean {
  const trimmed = term.trim();
  return trimmed.length === 0 || trimmed.length >= MIN_SEARCH_LENGTH;
}

export interface InventoryFilterFormValue {
  productName?: string;
  brandName?: string;
  productLocation?: string;
  category?: number | string;
  status?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc' | '';
  priceMin?: number;
  priceMax?: number;
  availableAmountMin?: number;
  availableAmountMax?: number;
  reservedAmountMin?: number;
  reservedAmountMax?: number;
  onHandAmountMin?: number;
  onHandAmountMax?: number;
}

const addRange = (
  filters: InventoryQueryParams,
  min: number | undefined,
  max: number | undefined,
  minKey: keyof InventoryQueryParams,
  maxKey: keyof InventoryQueryParams,
) => {
  const normalizedMin = normalizeRangeValue(min, FILTER_RANGE_DEFAULTS.MIN);
  const normalizedMax = normalizeRangeValue(max, FILTER_RANGE_DEFAULTS.MAX);

  if (normalizedMin !== FILTER_RANGE_DEFAULTS.MIN || normalizedMax !== FILTER_RANGE_DEFAULTS.MAX) {
    filters[minKey] = normalizedMin;
    filters[maxKey] = normalizedMax;
  }
};

const addTrimmedString = (
  filters: InventoryQueryParams,
  value: string | undefined,
  key: keyof InventoryQueryParams,
) => {
  const trimmedValue = value?.trim();
  if (trimmedValue) {
    filters[key] = trimmedValue;
  }
};

export function buildFilterParams(formValue: InventoryFilterFormValue): InventoryQueryParams {
  const filters: InventoryQueryParams = {};

  addTrimmedString(filters, formValue.productName, 'productName');
  addTrimmedString(filters, formValue.brandName, 'brandName');
  addTrimmedString(filters, formValue.productLocation, 'productLocation');

  if (formValue.category) {
    filters.categoryId = formValue.category as number;
  }

  if (formValue.status) {
    filters.status = formValue.status;
  }

  if (formValue.sortBy && formValue.sortOrder) {
    filters.sortBy = formValue.sortBy;
    filters.sortOrder = formValue.sortOrder;
  }

  addRange(filters, formValue.priceMin, formValue.priceMax, 'minPrice', 'maxPrice');

  addRange(
    filters,
    formValue.availableAmountMin,
    formValue.availableAmountMax,
    'minAvailableAmount',
    'maxAvailableAmount',
  );

  addRange(
    filters,
    formValue.reservedAmountMin,
    formValue.reservedAmountMax,
    'minReservedAmount',
    'maxReservedAmount',
  );

  addRange(
    filters,
    formValue.onHandAmountMin,
    formValue.onHandAmountMax,
    'minOnHandAmount',
    'maxOnHandAmount',
  );

  return filters;
}
